/**
 * SOLID Review
 * S — Strong. The pure transformations of a reveal lifted out of ControlPanel's subscription callbacks.
 *     Each answers one question and touches no store, adapter or React API.
 * O — Good. A new roll classification is a change in one function, not in a callback nested three
 *     levels inside a component.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Each takes exactly the values it reads.
 * D — Strong. Depends only on domain types.
 *
 * WHY THESE THREE, and not the whole component (2026-08-17). ControlPanel.tsx carried 403 surviving
 * mutants — the largest single gap in the UI tier — but 278 of them are in JSX/styles and 207 of
 * the 403 are `StringLiteral`: CSS values and static prose, the class stryker.ui.conf.json
 * explicitly says not to chase. The behavioural remainder concentrates in the four gameplay
 * subscriptions, and this file is that remainder made addressable. Extracting the JSX would have
 * moved the headline number without improving anything.
 *
 * MOVED to the Domain (frontend strict-ports plan, FE1, 2026-10-03), from
 * framework/components/game/controlPanel/revealTransforms.ts: the table decisions apply a reveal with
 * these, and the Domain cannot import the Framework. The roster merge went to `Roster.ts`, beside the
 * roster build it mirrors.
 */
import { GamePhase } from '../uml-components/primitives';
import type { IEntityClientDiceRoll } from '../ports/schemas/ClientTable';

/** The fields of a cup-lift / reveal payload that bear on the dice display. */
export interface IEntityRevealScores {
  readonly die1: number;
  readonly die2: number;
  readonly trueScore: number;
}

/**
 * Derive the displayable dice roll from a reveal payload.
 *
 * This existed TWICE in ControlPanel, byte for byte — once seeding a rejoin mid-REVEALING, once
 * handling the live cupLifted event — so a change to the classification could be applied to one
 * path and not the other.
 *
 * On the `isDouble` boundary: `>= 110 && < 1000` is the backend's rule verbatim
 * (PeekDiceUseCase.ts). It is reproduced rather than corrected on purpose. Note that it classifies
 * Little Meyer (900) as a double, which the frontend's own DiceRankingService disagrees with —
 * it labels only 110..160 as pairs, 900 being its own rank. Nothing in either workspace READS
 * `isDouble` (verified repo-wide: it is written, sent over GraphQL and asserted in tests, and
 * consumed by no production code), so the disagreement is latent, not live. Changing it would
 * alter a wire-visible field, which is not a change to slip into a refactor — recorded in the
 * architecture review instead.
 */
export function diceRollFromReveal(reveal: IEntityRevealScores): IEntityClientDiceRoll {
  return {
    die1: reveal.die1,
    die2: reveal.die2,
    score: reveal.trueScore,
    isMeyer: reveal.trueScore === 1000,
    isDouble: reveal.trueScore >= 110 && reveal.trueScore < 1000,
  };
}

/**
 * Which phase the table enters once the loser's life has been decremented.
 *
 * `remainingLives` is read from the store immediately AFTER the decrement, so 0 means the loser
 * has just been eliminated. The caller's `?? 1` default — preserved here as the `null`/`undefined`
 * case — is deliberately the NON-terminal value: an unknown player must not end the game. Getting
 * that default backwards would end a match on any roster inconsistency.
 */
export function phaseAfterCupLift(remainingLives: number | null | undefined): GamePhase {
  return (remainingLives ?? 1) <= 0 ? GamePhase.GAME_OVER : GamePhase.REVEALING;
}

/**
 * THE SERVER SAYS WHETHER THE GAME IS OVER (backend §28.191). The table decides it (the first player out,
 * handbook §3.4) and records it on the reveal; a client that judged it from its OWN copy of the lives —
 * decremented locally on every lift — called it wrong after a single missed event. Only a reveal from an
 * older server, which carries no `endsGame`, falls back to the local count.
 */
export function phaseAfterLift(reveal: { readonly endsGame?: boolean | null }, remainingLives: number | null | undefined): GamePhase {
  if (reveal.endsGame === true) return GamePhase.GAME_OVER;
  if (reveal.endsGame === false) return GamePhase.REVEALING;
  return phaseAfterCupLift(remainingLives);
}
